import React, { useState } from "react";
import { FaThumbsUp, FaThumbsDown } from "react-icons/fa"; // Replaced with filled icons
import { useAppSelector } from "../../redux/ReduxType";
import { useMakeRequest } from "../../hooks/useMakeRequest";
import { COMMENT_URL } from "../../constant/resource";
import { toast } from "sonner";
import { MdMoreVert } from "react-icons/md";
import LoaderSpinner from "./Loader/Loader.spinner";
import { useNavigate } from "react-router-dom";
import DEFAULT_PROFILE from "../../assets/others/avatar.jpeg";
import { timeAgo } from "../../utils/day.format";

interface CommentProps {
  id: number;
  name: string;
  authorId?: string;
  imgSrc?: string;
  timestamp: string;
  likes: string[];
  dislikes: string[];
  content: string;
  isDark?: boolean;
  refetch?: () => void;
  localRefetch?: () => void;
}

const Comment: React.FC<CommentProps> = ({
  id,
  authorId,
  name,
  imgSrc,
  timestamp,
  content,
  isDark = false,
  likes,
  dislikes,
  refetch,
  localRefetch,
}) => {
  const user = useAppSelector((state) => state.user);
  const [loadingDelete, setLoadingDelete] = useState<boolean>(false);
  const [openReport, setOpenReport] = useState<boolean>(false);
  const makeRequest = useMakeRequest();
  const navigate = useNavigate();

  const handleLike = () => {
    if (!user?._id) {
      toast.error("Sign in to continue");
      navigate("/register");
      return;
    }
    if (likes.includes(user?._id))
      return toast.error("You already liked this comment");
    const payload = { userId: user._id, commentId: id };
    makeRequest(
      COMMENT_URL + "/like",
      "POST",
      payload,
      () => {
        refetch && refetch();
        localRefetch && localRefetch();
      },
      () => toast.error("Error liking comment"),
      () => {}
    );
  };

  const handleDislike = () => {
    if (!user?._id) {
      toast.error("Sign in to continue");
      navigate("/register");
      return;
    }
    if (!likes.includes(user?._id))
      return toast.error("You haven’t liked this comment yet");
    const payload = { userId: user._id, commentId: id };
    makeRequest(
      COMMENT_URL + "/dislike",
      "POST",
      payload,
      () => {
        refetch && refetch();
        localRefetch && localRefetch();
      },
      () => toast.error("Error disliking comment"),
      () => {}
    );
  };

  const handleDeleteComment = () => {
    if (!user?._id) {
      toast.error("Sign in to continue");
      navigate("/register");
      return;
    }
    setLoadingDelete(true);
    const payload = { userId: user._id, author: authorId, commentId: id };
    makeRequest(
      COMMENT_URL + `/${user?._id}/${id}`,
      "DELETE",
      payload,
      () => {
        refetch && refetch();
        localRefetch && localRefetch();
        toast.success("Comment deleted");
      },
      () => toast.error("Error deleting comment"),
      () => setLoadingDelete(false)
    );
  };

  return (
    <article
      className={`flex gap-4 p-4 rounded-lg shadow-sm mb-4 ${
        isDark ? "bg-deepBg text-white" : "bg-white text-gray-900"
      }`}
    >
      <img
        className="w-10 h-10 rounded-full object-cover flex-shrink-0 ring-2 ring-gray-300 dark:ring-gray-600"
        src={imgSrc || DEFAULT_PROFILE}
        alt={`${name}'s avatar`}
      />

      <div className="flex-1 space-y-2">
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-sm">{name}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {timeAgo(timestamp)}
            </p>
          </div>
          <div className="dropdown dropdown-end">
            <div tabIndex={0} role="button">
              <MdMoreVert className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </div>
            <ul
              tabIndex={0}
              className={`dropdown-content menu rounded-lg shadow-lg w-40 p-2 ${
                isDark ? "bg-gray-700 text-white" : "bg-white text-gray-900"
              }`}
            >
              {authorId === user?._id && (
                <li>
                  <button
                    onClick={handleDeleteComment}
                    className="flex items-center gap-2"
                  >
                    {loadingDelete ? <LoaderSpinner color="gray" /> : "Delete"}
                  </button>
                </li>
              )}
              {authorId !== user?._id && (
                <li>
                  <button onClick={() => setOpenReport(true)}>Report</button>
                </li>
              )}
            </ul>
          </div>
        </header>

        <p
          className={`text-sm ${
            isDark ? "text-white" : "text-gray-800"
          } whitespace-pre-wrap break-words`}
        >
          {content}
        </p>

        <footer className="flex gap-6 pt-1">
          <button
            onClick={handleLike}
            className={`flex items-center gap-2 text-sm ${
              likes.includes(user?._id)
                ? "text-indigo-600 dark:text-indigo-400"
                : "text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
            } transition-colors duration-200`}
            aria-label={`Like comment (${likes.length} likes)`}
          >
            <FaThumbsUp className="w-3 h-3" />
            <span>{likes?.length || 0}</span>
          </button>
          <button
            onClick={handleDislike}
            className={`flex items-center gap-2 text-sm ${
              !likes.includes(user?._id) && dislikes.includes(user?._id)
                ? "text-red-600 dark:text-red-400"
                : "text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
            } transition-colors duration-200`}
            aria-label={`Dislike comment (${dislikes.length} dislikes)`}
          >
            <FaThumbsDown className="w-3 h-3" />
            <span>{dislikes?.length || 0}</span>
          </button>
        </footer>
      </div>
    </article>
  );
};

export default Comment;
